<template>
  <!-- The slot reserves layout width; the aside inside it is absolutely positioned so a hover
       expansion overlays the page instead of reflowing it. Pinned = slot grows to full width.
       Ported from SwiftProcessingSPA's SideNav.vue (see that repo for the fuller version with
       grouped/external nav items — trimmed here since none of SWIFT SPA's items need those). -->
  <div class="sidenav-slot" :class="{ 'is-pinned': pinned }">
    <aside
      class="sidenav"
      :class="{ 'is-expanded': expanded, 'is-pinned': pinned, 'is-hover': !pinned && hovering }"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
      @focusin="onEnter"
      @focusout="onFocusOut"
      aria-label="Main navigation"
    >
      <!-- Brand -->
      <div class="sidenav-brand-row">
        <!-- The logo is the way home: LoanFlow, for the loan that's open. -->
        <router-link :to="linkFor({ to: '/LoanTasks' })" class="sidenav-brand" title="LoanFlow">
          <!-- Collapsed rail: the single gold star from swfitLoan.svg (same path), not Font Awesome's sparkles. -->
          <span class="sidenav-brand-mark"><svg class="sidenav-brand-star" viewBox="15 13 289 289" aria-hidden="true"><path fill="#c69c39" d="M159.66,13.41c19.92,108.2,36.07,124.34,144.26,144.26-108.2,19.92-124.34,36.07-144.26,144.26-19.92-108.2-36.07-124.34-144.26-144.26,108.2-19.92,124.34-36.07,144.26-144.26"/></svg></span>
          <img src="../assets/swfitLoan.svg" alt="swiftLoan" class="sidenav-brand-logo" />
        </router-link>

        <!-- Pin / unpin — sits with the logo, only reachable when expanded -->
        <button
          type="button"
          class="sidenav-pin"
          :class="{ 'is-pinned': pinned }"
          :title="pinned ? 'Unpin sidebar (expand on hover)' : 'Pin sidebar open'"
          :aria-pressed="pinned ? 'true' : 'false'"
          @click="togglePin"
        >
          <i class="fa-solid fa-thumbtack"></i>
        </button>
      </div>

      <!-- Nav -->
      <nav class="sidenav-list">
        <router-link
          v-for="item in visibleItems"
          :key="item.label"
          :to="linkFor(item)"
          class="sidenav-item"
          :class="{ 'is-active': isActive(item.to) }"
          :title="expanded ? '' : item.label"
        >
          <span class="sidenav-icon"><i :class="item.icon"></i></span>
          <span class="sidenav-label">{{ item.label }}</span>
        </router-link>
      </nav>

      <!-- Footer actions -->
      <div class="sidenav-footer">
        <button type="button" class="sidenav-chat sidenav-chat--swift" title="Ask swiftAI" @click="openAskSwiftAI">
          <span class="sidenav-chat-icon"><i class="fa-duotone fa-wand-magic-sparkles"></i></span>
          <span class="sidenav-label">Ask swiftAI</span>
        </button>

        <!-- Opens this loan's LoanLaunch chat (LoanChatDrawer, hosted in App.vue). -->
        <button type="button" class="sidenav-chat sidenav-chat--ll" title="LoanLaunch Chat" @click="openLoanLaunchChat">
          <span class="sidenav-chat-icon">
            <i class="fa-duotone fa-messages"></i>
            <span v-if="unread > 0" class="sidenav-chat-dot"></span>
          </span>
          <span class="sidenav-label">Launch Chat</span>
          <span v-if="unread > 0" class="sidenav-chat-badge">{{ unread > 999 ? '999+' : unread }}</span>
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import bus from "@/helpers/appBus.js"
import {
  CHAT_KIND_LOANLAUNCH,
  CHAT_KIND_SWIFT,
  requestOpenChat,
  onChatUnread,
  offChatUnread
} from "@/helpers/chatLauncher.js"
import { readNavPinned, writeNavPinned, subscribeNavPinned } from "@/helpers/navPrefs.js"
import {
  NAV_ITEMS,
  computeNavPermissions,
  filterByPermissions,
  isPathActive
} from "@/helpers/navConfig.js"

// Hover intent: don't flash open when the cursor merely crosses the rail, and don't snap shut
// the instant it leaves.
const OPEN_DELAY_MS = 120
const CLOSE_DELAY_MS = 220

export default {
  name: "SideNav",
  data() {
    return {
      pinned: readNavPinned(),
      hovering: false,
      perms: computeNavPermissions(),
      openTimer: null,
      closeTimer: null,
      unsubscribePinned: null,
      unread: 0
    }
  },
  computed: {
    expanded() {
      return this.pinned || this.hovering
    },
    visibleItems() {
      return filterByPermissions(NAV_ITEMS, this.perms)
    },
    routePath() {
      return this.$route?.path || ""
    },
    loanId() {
      return this.$route?.query?.id || ""
    }
  },
  mounted() {
    // header.vue emits this once its role fetch resolves — SideNav can mount first, so the
    // Closing gate needs a chance to re-evaluate after roles actually land.
    bus.on("user-roles-ready", this.refreshPermissions)
    this.unsubscribePinned = subscribeNavPinned(v => { this.pinned = v })
    // LoanChatDrawer publishes its loan-scoped unread count on the bus; the rail badge mirrors it.
    onChatUnread(this.onUnread)
  },
  beforeUnmount() {
    bus.off("user-roles-ready", this.refreshPermissions)
    offChatUnread(this.onUnread)
    if (this.unsubscribePinned) this.unsubscribePinned()
    clearTimeout(this.openTimer)
    clearTimeout(this.closeTimer)
  },
  methods: {
    refreshPermissions() {
      this.perms = computeNavPermissions()
    },
    linkFor(item) {
      return { path: item.to, query: { id: this.loanId } }
    },
    isActive(to) {
      return isPathActive(this.routePath, to)
    },
    openAskSwiftAI() {
      // Opens SwiftChatDrawer (hosted in App.vue) — the same tabbed swiftChat flyout
      // SwiftProcessingSPA uses, pointed at ai.swiftapp.ai.
      requestOpenChat(CHAT_KIND_SWIFT)
    },
    openLoanLaunchChat() {
      requestOpenChat(CHAT_KIND_LOANLAUNCH)
    },
    onUnread(n) {
      this.unread = Number(n) || 0
    },
    togglePin() {
      this.pinned = !this.pinned
      writeNavPinned(this.pinned)
      // Unpinning while the mouse is still inside: stay open until it leaves.
      if (!this.pinned) this.hovering = true
    },
    onEnter() {
      clearTimeout(this.closeTimer)
      if (this.hovering || this.pinned) return
      this.openTimer = setTimeout(() => { this.hovering = true }, OPEN_DELAY_MS)
    },
    onLeave() {
      clearTimeout(this.openTimer)
      if (this.pinned) return
      this.closeTimer = setTimeout(() => { this.hovering = false }, CLOSE_DELAY_MS)
    },
    onFocusOut(e) {
      // Keyboard users: collapse only when focus actually leaves the sidebar.
      if (this.$el.contains(e.relatedTarget)) return
      this.onLeave()
    }
  }
}
</script>

<style scoped>
.sidenav-slot {
  --sn-collapsed: 72px;
  /* 210, not 186: "Launch Chat" plus the unread badge is the widest row in the rail. */
  --sn-expanded: 210px;
  --sn-ease: 180ms cubic-bezier(0.2, 0.7, 0.2, 1);

  position: relative;
  flex: 0 0 var(--sn-collapsed);
  width: var(--sn-collapsed);
  height: 100%;
  transition: flex-basis var(--sn-ease), width var(--sn-ease);
  z-index: 60;
}

.sidenav-slot.is-pinned {
  flex-basis: var(--sn-expanded);
  width: var(--sn-expanded);
}

.sidenav {
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--sn-collapsed);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid var(--gs-border, #e7ebf3);
  box-sizing: border-box;
  overflow: hidden;
  transition: width var(--sn-ease), box-shadow var(--sn-ease);
  font-family: inherit;
  -webkit-font-smoothing: antialiased;
}

.sidenav.is-expanded {
  width: var(--sn-expanded);
}

/* Hover-expanded (not pinned) floats over the page content */
.sidenav.is-hover {
  box-shadow: 10px 0 30px rgba(17, 40, 80, 0.08);
}

/* ── Brand ── */
.sidenav-brand-row {
  position: relative;
  display: flex;
  align-items: center;
  height: 62px;
  flex: 0 0 auto;
}

.sidenav-brand {
  display: flex;
  align-items: center;
  height: 62px;
  padding: 0 16px;
  gap: 9px;
  text-decoration: none;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.sidenav-brand-mark {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #e2a33c;
  font-size: 1.9rem;
}

.sidenav-brand-star {
  width: 24px;
  height: 24px;
  display: block;
}

.sidenav-brand-logo {
  height: 26px;
  width: auto;
  max-width: 120px;
  opacity: 0;
  transform: translateX(-6px);
  transition: opacity var(--sn-ease), transform var(--sn-ease);
}

.sidenav.is-expanded .sidenav-brand-logo {
  opacity: 1;
  transform: none;
}

/* Collapsed: the row is exactly the rail width, brand centered */
.sidenav:not(.is-expanded) .sidenav-brand {
  justify-content: center;
  padding: 0;
}

.sidenav:not(.is-expanded) .sidenav-brand-logo {
  display: none;
}

/* The logo SVG carries its own sparkle, so showing .sidenav-brand-mark alongside it gave the
   expanded sidebar two of them. The mark exists for the COLLAPSED rail, where the logo above
   is display:none -- so it is the one that goes away when the rail opens. */
.sidenav.is-expanded .sidenav-brand-mark {
  display: none;
}

.sidenav.is-expanded .sidenav-brand {
  padding-right: 42px;
}

.sidenav-pin {
  position: absolute;
  right: 8px;
  top: 50%;
  margin-top: -15px;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #b3bccb;
  font-size: 1.2rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--sn-ease), background 120ms ease, color 120ms ease, transform 150ms ease;
}

.sidenav.is-expanded .sidenav-pin {
  opacity: 1;
  pointer-events: auto;
}

.sidenav-pin i { transform: rotate(45deg); }
.sidenav-pin.is-pinned i { transform: none; }

.sidenav-pin:hover {
  background: #f2f5fa;
  color: var(--gs-blue, #2f6fed);
}

.sidenav-pin.is-pinned {
  color: var(--gs-blue, #2f6fed);
  background: var(--gs-blue-soft, #e9f0ff);
}

/* ── List ── */
.sidenav-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  scrollbar-width: thin;
}

.sidenav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  height: 42px;
  padding: 0 11px;
  border-radius: 9px;
  color: #6b7789;
  text-decoration: none;
  font-size: 1.3rem;
  font-weight: 500;
  white-space: nowrap;
  background: transparent;
  border: 0;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: background 120ms ease, color 120ms ease;
}

.sidenav-item:hover {
  background: #f4f7fc;
  color: var(--gs-ink, #16305a);
}

.sidenav-item.is-active {
  background: var(--gs-blue-soft, #e9f0ff);
  color: var(--gs-blue, #2f6fed);
  font-weight: 600;
}

.sidenav-icon {
  width: 22px;
  flex: 0 0 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: #97a2b3;
  transition: color 120ms ease;
}

.sidenav-item:hover .sidenav-icon { color: var(--gs-ink, #16305a); }
.sidenav-item.is-active .sidenav-icon { color: var(--gs-blue, #2f6fed); }

.sidenav-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  transform: translateX(-6px);
  transition: opacity var(--sn-ease), transform var(--sn-ease);
}

.sidenav.is-expanded .sidenav-label {
  opacity: 1;
  transform: none;
}

/* ── Footer ── */
.sidenav-footer {
  flex: 0 0 auto;
  padding: 10px 10px 14px;
  border-top: 1px solid var(--gs-border-soft, #eef1f7);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidenav-chat {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  height: 42px;
  width: 100%;
  padding: 0 7px;
  border-radius: 9px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--gs-ink, #16305a);
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms ease;
}

.sidenav-chat:hover { background: #f4f7fc; }

.sidenav-chat-icon {
  position: relative;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  background: var(--gs-navy, #15305a);
  color: #fff;
}

.sidenav-chat--swift .sidenav-chat-icon i {
  --fa-primary-color: #7dd3fc;
  --fa-secondary-color: #fde68a;
  --fa-secondary-opacity: 1;
}

.sidenav-chat--ll .sidenav-chat-icon {
  background: var(--gs-blue, #2f6fed);
}

/* Unread dot rides the icon so it stays visible when the rail is collapsed and
   the numeric badge (which sits after the label) is clipped away. */
.sidenav-chat-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--gs-red, #e23c3c);
  border: 2px solid #fff;
  box-sizing: content-box;
}

/* Collapsed rail is 72px and clips: the badge would bleed a red sliver at the edge,
   so only the dot survives the collapse. */
.sidenav:not(.is-expanded) .sidenav-chat-badge {
  display: none;
}

.sidenav-chat-badge {
  margin-left: auto;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: var(--gs-red, #e23c3c);
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

/* ── Mobile: hide the rail; header.vue falls back to its horizontal nav ── */
@media (max-width: 630px) {
  .sidenav-slot {
    display: none;
  }
}
</style>
